// ═══════════════════════════════════════════════════════════════
// PA Grants — Applicant Portal Dashboard
// Scoped summary: org info, project pipeline, recent activity
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, SimpleGrid, Button, Progress, Table,
} from '@mantine/core';
import { IconClipboardList, IconAlertTriangle, IconExternalLink, IconReceipt } from '@tabler/icons-react';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { useMyPAProjects, useMyPADamageItems, useMyPADocuments, useMyPAReimbursements } from '../../../hooks/useMyPAOrgData';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { PROJECT_PHASE_LABELS } from '../../../types/pagrants';

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const PHASE_COLORS: Record<string, string> = {
  formulation: 'blue', review: 'yellow', obligation: 'green',
  work_in_progress: 'orange', closeout: 'violet', appeal: 'red',
};

export default function PortalDashboard() {
  const navigate = useNavigate();
  const myOrg = useMyPAOrg();
  const myProjects = useMyPAProjects();
  const myDamage = useMyPADamageItems();
  const myDocs = useMyPADocuments();
  const myRFRs = useMyPAReimbursements();
  const organizations = usePAGrantsStore(s => s.organizations);

  // Sub-applicant orgs (if grantee)
  const subOrgs = useMemo(() => {
    if (!myOrg || myOrg.orgRole !== 'grantee') return [];
    return organizations.filter(o => String(o.parentOrgId) === String(myOrg.Rid));
  }, [myOrg, organizations]);

  // Stats
  const totalEstimated = useMemo(
    () => myProjects.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0),
    [myProjects],
  );
  const totalObligated = useMemo(
    () => myProjects.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0),
    [myProjects],
  );

  // Phase breakdown
  const phaseBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    myProjects.forEach(p => { counts[p.phase] = (counts[p.phase] || 0) + 1; });
    return counts;
  }, [myProjects]);

  if (!myOrg) return null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>{myOrg.orgName}</Title>
          <Text size="sm" c="dimmed">
            {myOrg.orgRole === 'grantee' ? 'Grantee' : myOrg.orgRole === 'subgrantee' ? 'Sub-Grantee' : 'Applicant'}
            {myOrg.orgType ? ` — ${myOrg.orgType}` : ''}
          </Text>
        </div>
        {subOrgs.length > 0 && (
          <Badge variant="light" color="blue" size="lg">
            {subOrgs.length} Sub-Applicant{subOrgs.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </Group>

      {/* Stats cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <Paper withBorder p="md" radius="md">
          <Group gap="xs" mb={4}>
            <IconClipboardList size={16} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Projects</Text>
          </Group>
          <Text size="xl" fw={700}>{myProjects.length}</Text>
        </Paper>
        <Paper withBorder p="md" radius="md">
          <Group gap="xs" mb={4}>
            <IconAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Damage Sites</Text>
          </Group>
          <Text size="xl" fw={700}>{myDamage.length}</Text>
        </Paper>
        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Estimated Cost</Text>
          <Text size="xl" fw={700}>{fmt(totalEstimated)}</Text>
        </Paper>
        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Obligated</Text>
          <Text size="xl" fw={700} c={totalObligated > 0 ? 'green' : undefined}>{fmt(totalObligated)}</Text>
        </Paper>
      </SimpleGrid>

      {/* Getting Started tracker */}
      {(() => {
        const steps = [
          { label: 'Organization verified', done: !!myOrg.primaryEmail, detail: 'Contact info and FEMA ID on file' },
          { label: 'Damage sites documented', done: myDamage.length > 0, detail: `${myDamage.length} site${myDamage.length !== 1 ? 's' : ''} logged` },
          { label: 'Projects created', done: myProjects.length > 0, detail: `${myProjects.length} project${myProjects.length !== 1 ? 's' : ''} created` },
          { label: 'Insurance documentation', done: myProjects.some(p => parseFloat(p.insuranceDeduction || '0') >= 0 && p.insuranceDeduction !== ''), detail: 'Upload insurance policies and claim info' },
          { label: 'Documents uploaded', done: myDocs.length >= 3, detail: `${myDocs.length} document${myDocs.length !== 1 ? 's' : ''} uploaded` },
          { label: 'Reimbursement submitted', done: myRFRs.length > 0, detail: myRFRs.length > 0 ? `${myRFRs.length} RFR${myRFRs.length !== 1 ? 's' : ''} submitted` : 'Submit your first Request for Reimbursement' },
        ];
        const doneCount = steps.filter(s => s.done).length;
        const allDone = doneCount === steps.length;

        if (allDone) return null; // Hide when everything is done

        return (
          <Paper withBorder p="md" radius="md" style={{ borderLeft: '4px solid var(--mantine-color-blue-5)' }}>
            <Group justify="space-between" mb="sm">
              <Text size="sm" fw={600}>Getting Started</Text>
              <Badge size="sm" variant="light" color={doneCount === steps.length ? 'green' : 'blue'}>
                {doneCount}/{steps.length} complete
              </Badge>
            </Group>
            <Progress value={(doneCount / steps.length) * 100} size="sm" color="blue" mb="sm" />
            <Stack gap={4}>
              {steps.map((step, i) => (
                <Group key={i} gap="sm" p={4} style={{ opacity: step.done ? 0.6 : 1 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: step.done ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-3)',
                    color: 'white', fontSize: 11, fontWeight: 700 }}>
                    {step.done ? '✓' : i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={500} td={step.done ? 'line-through' : undefined}>{step.label}</Text>
                    <Text size="xs" c="dimmed">{step.detail}</Text>
                  </div>
                </Group>
              ))}
            </Stack>
          </Paper>
        );
      })()}

      {/* Project pipeline */}
      <Paper withBorder p="md" radius="md">
        <Text size="sm" fw={600} mb="sm">Project Pipeline</Text>
        <Group gap="md">
          {Object.entries(PROJECT_PHASE_LABELS).map(([phase, label]) => {
            const count = phaseBreakdown[phase] || 0;
            return (
              <Paper key={phase} p="xs" radius="sm" style={{ textAlign: 'center', minWidth: 100 }}>
                <Text size="xl" fw={700}>{count}</Text>
                <Badge size="xs" color={PHASE_COLORS[phase] || 'gray'} variant="light">{label}</Badge>
              </Paper>
            );
          })}
        </Group>
      </Paper>

      {/* Action items — projects and RFRs needing attention */}
      {(() => {
        const drafts = myProjects.filter(p => p.status === 'draft');
        const inReview = myProjects.filter(p => p.status === 'in_review');
        const revisionRFRs = myRFRs.filter(r => r.status === 'revision_needed');
        const draftRFRs = myRFRs.filter(r => r.status === 'draft');
        const approvedRFRs = myRFRs.filter(r => r.status === 'approved' || r.status === 'paid');
        if (drafts.length === 0 && inReview.length === 0 && revisionRFRs.length === 0 && draftRFRs.length === 0 && approvedRFRs.length === 0) return null;
        return (
          <Paper withBorder p="md" radius="md" style={{ borderLeft: '3px solid var(--mantine-color-orange-5)' }}>
            <Text size="sm" fw={600} mb="sm">Action Items</Text>
            <Stack gap="xs">
              {drafts.map(p => (
                <Group key={p.Rid} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" color="gray" variant="dot">Draft</Badge>
                    <Text size="sm" style={{ cursor: 'pointer' }} c="blue"
                      onClick={() => navigate(`/pagrants/pa/projects/${p.Rid}`)}>
                      {p.projectNumber} — {p.projectTitle}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">Needs scope, costs, documents</Text>
                </Group>
              ))}
              {inReview.map(p => (
                <Group key={p.Rid} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" color="yellow" variant="dot">In Review</Badge>
                    <Text size="sm" style={{ cursor: 'pointer' }} c="blue"
                      onClick={() => navigate(`/pagrants/pa/projects/${p.Rid}`)}>
                      {p.projectNumber} — {p.projectTitle}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">Awaiting FEMA review</Text>
                </Group>
              ))}
              {revisionRFRs.map(r => (
                <Group key={r.Rid} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" color="orange" variant="dot">Revision Needed</Badge>
                    <Text size="sm" style={{ cursor: 'pointer' }} c="blue"
                      onClick={() => navigate('/pagrants/pa/reimbursements')}>
                      <IconReceipt size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{r.requestNumber} — {fmt(parseFloat(r.requestedAmount) || 0)}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">{r.reviewNotes ? r.reviewNotes.substring(0, 50) : 'Revisions requested'}</Text>
                </Group>
              ))}
              {draftRFRs.map(r => (
                <Group key={r.Rid} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" color="gray" variant="dot">RFR Draft</Badge>
                    <Text size="sm" style={{ cursor: 'pointer' }} c="blue"
                      onClick={() => navigate('/pagrants/pa/reimbursements')}>
                      {r.requestNumber} — {fmt(parseFloat(r.requestedAmount) || 0)}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">Ready to submit</Text>
                </Group>
              ))}
              {approvedRFRs.map(r => (
                <Group key={r.Rid} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" color="green" variant="dot">RFR Approved</Badge>
                    <Text size="sm" style={{ cursor: 'pointer' }} c="blue"
                      onClick={() => navigate('/pagrants/pa/reimbursements')}>
                      {r.requestNumber} — {fmt(parseFloat(r.approvedAmount) || 0)}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {(r as any).status === 'paid'
                      ? `Paid${(r as any).paymentReference ? ` — Ref: ${(r as any).paymentReference}` : ''}`
                      : 'Approved, awaiting payment'}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Paper>
        );
      })()}

      {/* My Projects list — clickable into workspace */}
      {myProjects.length > 0 && (
        <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
          <Group justify="space-between" p="sm" pb={0}>
            <Text size="sm" fw={600}>My Projects</Text>
            <Button variant="subtle" size="xs" onClick={() => navigate('/pagrants/pa/projects')}>View All</Button>
          </Group>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>PW #</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Phase</Table.Th>
                <Table.Th>Completion</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Estimated</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Docs</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {myProjects.slice(0, 10).map(p => {
                const pctNum = parseFloat(p.completionPctEstimate) || 0;
                const docCount = myDocs.filter(d => d.projectId === String(p.Rid)).length;
                return (
                  <Table.Tr key={p.Rid} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/pagrants/pa/projects/${p.Rid}`)}>
                    <Table.Td><Text size="sm" fw={600}>{p.projectNumber}</Text></Table.Td>
                    <Table.Td><Text size="sm">{p.projectTitle}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={PHASE_COLORS[p.phase] || 'gray'} variant="light">
                        {PROJECT_PHASE_LABELS[p.phase as keyof typeof PROJECT_PHASE_LABELS] ?? p.phase}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ minWidth: 100 }}>
                      <Group gap={4}>
                        <Progress value={pctNum} size="sm" style={{ flex: 1 }} color={pctNum >= 100 ? 'green' : 'blue'} />
                        <Text size="xs" c="dimmed">{pctNum}%</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmt(parseFloat(p.estimatedCost) || 0)}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Badge size="xs" variant="light">{docCount}</Badge>
                    </Table.Td>
                    <Table.Td><IconExternalLink size={14} color="var(--mantine-color-dimmed)" /></Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Sub-applicants summary (grantee only) */}
      {subOrgs.length > 0 && (
        <Paper withBorder p="md" radius="md">
          <Text size="sm" fw={600} mb="sm">Sub-Applicant Organizations</Text>
          <Stack gap="xs">
            {subOrgs.map(org => {
              const orgProjects = myProjects.filter(p => String(p.organizationId) === String(org.Rid));
              return (
                <Group key={org.Rid} justify="space-between">
                  <Text size="sm">{org.orgName}</Text>
                  <Group gap="xs">
                    <Badge size="sm" variant="light">{orgProjects.length} project{orgProjects.length !== 1 ? 's' : ''}</Badge>
                    <Badge size="sm" variant="light" color="gray">{org.orgType}</Badge>
                  </Group>
                </Group>
              );
            })}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
